import React, { useEffect, useState } from 'react';
import { Check, ChevronRight } from 'lucide-react';

/**
 * Closing screen for every engagement — joining, enrolling, registering,
 * applying or donating. A morphing button confirms that the tap registered,
 * but people also need to know what they just committed to and what happens
 * next, which a button label and a toast cannot carry.
 */
export default function EngagementConfirmation({
  title,
  subtitle,
  itemTitle,
  itemImage,
  meta = [],
  nextSteps = [],
  primaryLabel = 'Done',
  onPrimary,
  secondaryLabel,
  onSecondary
}) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      className="min-h-screen bg-background page-fade-in flex flex-col"
      data-testid="engagement-confirmation-page"
    >
      <div className="flex-1 px-5 pt-12 pb-8 md:pt-20 max-w-lg mx-auto w-full">
        <div className="flex flex-col items-center text-center">
          <span
            className={`flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 transition-all duration-500 ease-out ${
              shown ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
            }`}
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-emerald-400 text-white">
              <Check className="h-7 w-7" strokeWidth={3} />
            </span>
          </span>

          <h1 className="mt-5 text-2xl font-extrabold text-[#0F172A] leading-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-muted-foreground leading-snug">{subtitle}</p>}
        </div>

        <div className="mt-7 rounded-2xl border border-border bg-white overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-border/60">
            {itemImage && (
              <img
                src={itemImage}
                alt=""
                className="h-12 w-12 rounded-lg object-cover flex-shrink-0"
              />
            )}
            <p className="text-sm font-bold text-[#0F172A] leading-snug">{itemTitle}</p>
          </div>
          {meta.length > 0 && (
            <dl className="divide-y divide-border/60">
              {meta.map((m) => (
                <div key={m.label} className="flex items-start justify-between gap-4 px-4 py-2.5">
                  <dt className="text-xs text-muted-foreground">{m.label}</dt>
                  <dd className="text-xs font-semibold text-[#0F172A] text-right">{m.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {nextSteps.length > 0 && (
          <div className="mt-6">
            <h2 className="text-sm font-bold text-[#0F172A] mb-3">What happens next</h2>
            <ol className="flex flex-col gap-3">
              {nextSteps.map((step, i) => (
                <li key={step} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                    {i + 1}
                  </span>
                  <span className="text-sm leading-snug text-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-2.5">
          <button
            type="button"
            data-testid="confirmation-primary"
            onClick={onPrimary}
            className="h-12 w-full rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover transition-colors"
          >
            {primaryLabel}
          </button>
          {secondaryLabel && (
            <button
              type="button"
              data-testid="confirmation-secondary"
              onClick={onSecondary}
              className="h-12 w-full rounded-xl border border-border font-semibold text-sm text-[#0F172A] hover:bg-muted transition-colors inline-flex items-center justify-center gap-1"
            >
              {secondaryLabel}
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
